import { AccountingData, Dates, LedgerEntry, Translations, Language } from "@salaxy/core";
import { HeaderFooterData, ReportOptions, ReportLayout } from "../../model";
import { AccountingReport } from "./AccountingReport";
import { IAccountingReportResultRow } from "./IAccountingReportResultRow";

/**
 * Mapper for accounting report.
 */
export class AccountingMapper {

  /**
   * Maps data to hbs supported report format
   * @param data BusinessObject data for mapping
   * @returns Mapped data
   */
  public static getAccountingReport(data: AccountingData): AccountingReport {
    const accountingReport: AccountingReport = data;
    accountingReport.result = [];
    accountingReport.dimensionHeaders = [];
    accountingReport.globalDimensions = [];
    const addToGlobal = (value, name) => {
      if (value) {
        if (!accountingReport.globalDimensions?.includes(name) && name !== "costCenter") {
          accountingReport.globalDimensions!.push(name);
        }
      }
    };
    // Find all dimensions
    for (const rowData of data.ledgerAccounts ?? []) {
      for (const entryData of rowData.entries ?? []) {
        const costCenter = entryData.dimension?.costCenter || entryData.dimension?.dimensions?.costCenter;
        addToGlobal(costCenter, "SALAXY.REPORTS.accounting.heading.costCenter");
        if (entryData.dimension?.dimensions) {
          for (const [key, value] of Object.entries(entryData.dimension.dimensions)) {
            addToGlobal(value, key);
          }
        }

      }
    }
    accountingReport.dimensionHeaders = accountingReport.globalDimensions;
    for (const rowData of data.ledgerAccounts ?? []) {
      const keyEntriesRows: {
        /** Entries */
        entries: LedgerEntry[],
        /** VAT */
        vatPercent: number,
        /** Dimensions */
        dimensions: any[],
        /** DimensionsId */
        dimensionsId: string
      }[] = [];
      for (const entryData of rowData.entries ?? []) {
        const rowDataEntriesDimensions: string[] = [];
        for (const globalDimension of accountingReport.globalDimensions) {
          if (globalDimension === "SALAXY.REPORTS.accounting.heading.costCenter") {
            const costCenter = entryData.dimension?.costCenter || entryData.dimension?.dimensions?.costCenter || "";
            rowDataEntriesDimensions.push(costCenter);
          } else {
            rowDataEntriesDimensions.push(entryData.dimension?.dimensions ? entryData.dimension.dimensions[globalDimension] : "");
          }

        }
        const vatPercent = entryData.vatPercent ?? 0;
        let keyEntryRows: {
          /** Entries */
          entries: LedgerEntry[],
          /** VAT */
          vatPercent: number,
          /** Dimensions */
          dimensions: any[],
          /** DimensionsId */
          dimensionsId: string
        } | undefined = keyEntriesRows.find((x) => x.vatPercent === vatPercent && x.dimensionsId === rowDataEntriesDimensions.join("-"));
        if (keyEntryRows == null) {
          keyEntryRows = { entries: [], vatPercent, dimensions: rowDataEntriesDimensions, dimensionsId: rowDataEntriesDimensions.join("-") };
          keyEntriesRows.push(keyEntryRows);
        }

        keyEntryRows.entries.push(entryData);
      }
      // Append results
      for (const keyEntryRows of keyEntriesRows) {
        const row: IAccountingReportResultRow = {};
        const dimensions: any = {};
        for (let i = 0; i < accountingReport.globalDimensions.length; i++) {
          const key = accountingReport.globalDimensions[i];
          dimensions[key] = keyEntryRows.dimensions[i];
        }
        row.dimensions = dimensions;
        row.accountNumber = rowData.accountNumber;
        row.accountName = rowData.accountName;
        row.vatPercent = keyEntryRows.vatPercent;
        const saldo = keyEntryRows.entries?.reduce((sum, x) => sum + (x?.isDebit ? x?.amount ?? 0 : -1.0 * (x?.amount ?? 0)), 0) ?? 0;
        row.sum = saldo;
        accountingReport.result.push(row);
      }
    }
    return accountingReport;
  }

  /**
   * Creates HeaderFooterData from provided parameters
   * @param businessObject AccountingData that
   * @param options Report options
   * @param lang gets translation with this lang, if null use current lang
   * @returns HeaderFooterData for provided businessObject
   */
  public static getHeaderFooter(businessObject: AccountingData, options: ReportOptions, lang: Language): HeaderFooterData {
    const headerFooter: HeaderFooterData = {
      logo: options?.logo,
      img2: options?.img2,
      title: Translations.get("SALAXY.REPORTS.accounting.title", null, lang),
      today: Dates.getFormattedDate(Dates.getToday()),
      date: Dates.getFormattedDate(Dates.getToday())
    };
    return headerFooter;
  }

  /**
   * Creates ReporLayout object depending on switchToPortrait boolean
   * @param switchToPortrait boolean indicating if layout should be portrait
   * @returns ReportLayout object
   */
  public static getReportLayout(switchToPortrait: boolean, pdf = false): ReportLayout {
    if (switchToPortrait) {
      return {
        isLayoutA4Portrait: true,
        isLayoutA4Landscape: false,
        layout: "a4Portrait",
        date: Dates.getFormattedDate(Dates.getToday()),
        isPreview: false,
        mode: pdf ? "pdf" : "htmlPage",
        isModeHtmlPage: !pdf,
        isModePdf: pdf,
      };
    } else {
      return {
        isLayoutA4Portrait: false,
        isLayoutA4Landscape: true,
        layout: "a4Landscape",
        date: Dates.getFormattedDate(Dates.getToday()),
        isPreview: false,
        mode: pdf ? "pdf" : "htmlPage",
        isModeHtmlPage: !pdf,
        isModePdf: pdf,
      };
    }
  }
}